import useGetOnboardStatus from "lib/hooks/useGetOnboardStatus";
import { useEffect } from "react";
import Router from "next/router";
import { useAppDispatch } from "../../redux/hooks";
import {
  login,
  markAuthChecked,
  updateOnboardStatus,
  updateSubscribeStatus,
} from "../../redux/reducers/userSlice";
import parseJwt from "../../utils/helper/jwtParser";
import { getAccessToken } from "../accessToken";
import useGetMe from "../hooks/useGetMe";
import useGetUserSubscription from "lib/hooks/useGetUserSubscriptions";
import useSWR from "swr";
import { getUserSubscriptions } from "lib/common/endpoints/endUser";

interface props {
  children: React.ReactNode;
  button?: boolean;
  fixed?: boolean;
}
function withTryLoggingIn(Component: React.FC<props>) {
  // eslint-disable-next-line react/display-name
  return ({ children, button, fixed }: props) => {
    const { data, loading } = useGetMe();
    const { data: onBoardingStatus, loading: onBoardLoading } =
      useGetOnboardStatus();

    const { data: userSubscription } = useSWR(
      data ? getUserSubscriptions : null
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
      const ac = new AbortController();
      if (data) {
        dispatch(
          login({
            user: data,
            isAdmin: parseJwt(getAccessToken() ?? "").isAdminUser ?? false,
            isSuperAdmin:
              parseJwt(getAccessToken() ?? "").isSuperAdminUser ?? false,
          })
        );
      }
      return () => ac.abort();
    }, [data, dispatch]);

    useEffect(() => {
      const ac = new AbortController();
      if (onBoardingStatus) {
        dispatch(
          updateOnboardStatus({
            onBoardingStatus: onBoardingStatus,
          })
        );
      }
      return () => ac.abort();
    }, [dispatch, onBoardingStatus, data]);

    useEffect(() => {
      const ac = new AbortController();
      if (userSubscription) {
        dispatch(
          updateSubscribeStatus({
            subscriptionsStatus: userSubscription.data,
          })
        );
      }
      return () => ac.abort();
    }, [dispatch, userSubscription, onBoardingStatus, data]);

    // If loading finished but there is no user, mark subscriptions as loaded
    // (with empty list) so pages gated on subscriptionsLoaded don't stay
    // stuck on a skeleton state forever, and mark the auth check itself as
    // resolved -- login()/onBoardUser() already do this for the "logged
    // in" outcome, this covers "definitively not logged in" so pages can
    // tell "still checking" (authChecked: false) apart from "checked, and
    // you're logged out" instead of flashing the logged-out UI on every
    // page load before the check has even run.
    useEffect(() => {
      if (!loading && !onBoardLoading && !data) {
        dispatch(updateSubscribeStatus({ subscriptionsStatus: [] }));
        dispatch(markAuthChecked());
      }
    }, [loading, onBoardLoading, data, dispatch]);

    // IMPORTANT: never gate rendering on this login check. `loading` starts
    // `true` and only flips during a client-only useEffect, so during
    // SSR/SSG this was always true -- every page wrapped by this HOC
    // (via pages/layout/default.tsx, i.e. nearly every marketing page on
    // the site) was statically generating with NOTHING but a loading
    // spinner in its HTML. No hero, no headings, no links -- just a
    // spinner, for every crawler (Googlebot, Bingbot, AI crawlers) that
    // doesn't wait out a full client-side hydration + auth round trip.
    // The async login/onboarding/subscription checks above still run and
    // dispatch to Redux exactly as before; only the "don't render
    // children until this resolves" block is gone.
    return (
      <Component button={button} fixed={fixed}>
        {children}
      </Component>
    );
  };
}

export default withTryLoggingIn;
